import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import { type LineItem } from "~/feature/cart/cart/models/CartData";
import { DELIVERY_DATE_OFFSET } from "./constants";
import { type DateFormatType, formatDate } from "~/utils/DateFormatter";
import instance from "~/utils/i18n";

import { isNonNull } from "~/utils/reducers";
import type { ContractDetailsResponse } from "~/Contracts/models";
import { Status, STATUS_CODES_MAP } from "~/Contracts/components/AddToCartPopup/ContractVersionContents";
import { showDateAsCalendarWeek } from "./features";

dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);

const isSundayEnabledForDelivery = window.SCHA.feature.showSundayAsDeliveryDay;

// 400 years have 146097 days (taking into account leap year rules)
// 400 years have 12 months === 4800
const DAY_MONTH_RATIO = 4800 / 146097;

const MS_DAY_RATIO = 1 / (1000 * 60 * 60 * 24);

// Taken from https://github.com/moment/moment/blob/develop/src/lib/duration/bubble.js
export function daysToMonths(days: number): number {
  // 400 years have 146097 days (taking into account leap year rules)
  // 400 years have 12 months === 4800
  return days * DAY_MONTH_RATIO;
}

export function msToMonths(ms: number): number {
  return ms * MS_DAY_RATIO * DAY_MONTH_RATIO;
}

/**
 * Returns true if date is invalid for delivery, that means:
 *
 * * it's in the past
 * * it's more than two years ahead
 * * it's a Sunday
 */
export function isDateInvalidForDelivery(date: Date): boolean {
  const m = dayjs(date);

  const validatorList = [isBeforeMinimumDate, isTooFarFuture];
  if (!isSundayEnabledForDelivery) {
    validatorList.push(isSunday);
  }

  const validators = validatorList.filter(isNonNull);

  return validators.some((validator) => validator(m));
}

export function cartIsDayDisabled(lineItem: LineItem, date: Date) {
  if (isDateInvalidForDelivery(date)) {
    return true;
  }

  const m = dayjs(date);
  const { minRdd, maxRdd } = lineItem;

  let isDisabled = false;
  if (minRdd) {
    isDisabled = isDisabled || m.isBefore(minRdd);
  }
  if (maxRdd) {
    isDisabled = isDisabled || m.isAfter(maxRdd);
  }
  if (lineItem.saleCampaignEnabled && window.SCHA.salesCampaignDaysLimitRDD) {
    const maxDay = addWorkingDays(dayjs(), window.SCHA.salesCampaignDaysLimitRDD);
    isDisabled = isDisabled || m.isAfter(maxDay);
  }
  return isDisabled;
}

export const getContractYears = (contractDetails: ContractDetailsResponse) => {
  let maxYear = undefined;
  let minYear = undefined;

  if (STATUS_CODES_MAP[contractDetails.status as keyof typeof STATUS_CODES_MAP] === Status.Active) {
    if (contractDetails.contractValidTo) {
      maxYear = dayjs(contractDetails.contractValidTo).get("year");
    }
    if (contractDetails.contractValidFrom) {
      minYear = dayjs(contractDetails.contractValidFrom).get("year");
      minYear = Math.max(minYear, dayjs().toDate().getFullYear());
    }
  }
  return { minYear, maxYear };
};

export function contractIsDayDisabled(contractDetails: ContractDetailsResponse, date: Date) {
  if (isDateInvalidForDelivery(date)) {
    return true;
  }

  const { minYear, maxYear } = getContractYears(contractDetails);

  const m = dayjs(date);

  let isDisabled = false;
  if (minYear) {
    isDisabled = isDisabled || m.isBefore(contractDetails.contractValidFrom, "y");
  }
  if (maxYear && minYear !== maxYear) {
    isDisabled = isDisabled || m.isAfter(contractDetails.contractValidTo, "y");
  }

  return isDisabled;
}

export const isSunday = (d: dayjs.Dayjs) => d.get("day") === 0;

function calculateMinimumRDD() {
  return addRDDOffset(dayjs());
}

export function addRDDOffset(date: Date | dayjs.Dayjs) {
  return addWorkingDays(date, DELIVERY_DATE_OFFSET);
}

export function addWorkingDays(date: Date | dayjs.Dayjs, amount: number): dayjs.Dayjs {
  let d = dayjs(date);
  const fullWeeks = Math.floor(amount / 6);
  const remainder = amount - fullWeeks * 6;
  const offset = fullWeeks * 7 + remainder;
  const dayStart = d.get("day");
  d = d.add(offset, "day");
  const dayEnd = d.get("day");
  if (!isSundayEnabledForDelivery && remainder > 0 && dayEnd < dayStart) {
    d = d.add(1, "day");
  }
  return d;
}

export const TODAY = dayjs();

export const MINIMUM_RDD = calculateMinimumRDD();
export const isBeforeMinimumDate = (d: dayjs.Dayjs) => d.isBefore(MINIMUM_RDD, "day");

const LAST_VALID_DATE = MINIMUM_RDD.add(window.SCHA.rddMaxYearsAhead ?? 2, "year");
export const isTooFarFuture = (d: dayjs.Dayjs) => d.isAfter(LAST_VALID_DATE, "day");

export const getFirstValidDate = () => {
  return MINIMUM_RDD.toDate();
};

export const getCurrentBrowserTimezone = () => {
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    timezone = "UTC";
  }
  return timezone;
};

export const getNextValidDeliveryDate = (d: dayjs.Dayjs) => {
  if (isBeforeMinimumDate(d)) {
    return MINIMUM_RDD;
  }
  if (!isSundayEnabledForDelivery && isSunday(d)) {
    return d.add(1, "day");
  }
  return d;
};

export const getLastValidDate = () => {
  return LAST_VALID_DATE.toDate();
};

export function formatShippingOrDeliveryDate(
  date: Date | undefined | null,
  type: DateFormatType,
  missingDateSymbol: string = "-",
) {
  if (!date) {
    return missingDateSymbol;
  }
  if (showDateAsCalendarWeek()) {
    const dateInput = dayjs(date);
    return `${instance.t("feature.shipdate.to.iso.week")}${dateInput.week()}/${dateInput.year()}`;
  }
  return formatDate(date, type);
}
